(function () {
  'use strict';

  // ── LAYER 3: the reward APPLICATOR — issues the resolved offer's reward ──
  //
  // The offer object is only real if its reward actually happens in cart +
  // checkout. This is the ONE place that turns window.__activeOffer.reward into a
  // store action, via the two primitives that already exist in this theme:
  //   - apply a code  -> /checkout?discount=CODE   (discount_code, bxgy_code, gwp_coded)
  //   - add a variant -> /cart/add.js              (gwp_free_variant, gwp_coded)
  //
  // WRITE DISCIPLINE (2026-08-11 sweep): all cart writes flow through syncAll —
  // sequential, response-verified (fetch resolves on 4xx/429, so every response is
  // ok-checked), at most ONE cart:refresh per pass, and a consecutive-failure
  // breaker so a persistently-failing write (sold-out gift variant, deleted code,
  // rate limiting) can never become a request loop. Individual writers remain
  // callable (tests, targeted use) and then manage their own refresh.
  //
  // Reward types that carry a code the theme applies at checkout:
  var CODE_REWARD_TYPES = { discount_code: 1, bxgy_code: 1, gwp_coded: 1 };

  // Live-probed (2026-08-10): /discount/CODE APPENDS to the cart's discount_codes
  // array (multi-code carts) — it does NOT replace. The working removal is
  // POST /cart/update.js {discount: ""} which empties the array. So clearing =
  // update.js, arming = /discount/, switching = clear then arm.

  function assertOk(r) {
    if (!r || r.ok === false) throw new Error('cart write failed' + (r && r.status ? ' (' + r.status + ')' : ''));
    return r;
  }

  function refreshCart() {
    if (typeof document !== 'undefined') document.dispatchEvent(new Event('cart:refresh'));
  }
  function wantRefresh(opts) {
    return !(opts && opts.refresh === false);
  }

  // Count the set pieces that BELONG TO the given offer (sweep H1: a builder set
  // and a preconfig set in the same cart must not inflate each other's counts).
  //   exact-trigger (preconfig) -> only lines carrying ITS stamp.
  //   the GENERIC set offer -> the UNION (Sean's unified model, 2026-08-12):
  //     stamped non-preconfig lines (builder provenance) OR lines whose product
  //     is in the offer's eligible collection (trigger.productIds — PDP/recs
  //     adds count too; display, tier, and the collection-scoped code's server
  //     floors finally all agree). Gift lines never count.
  // No offer given -> legacy behavior (all _bundle lines).
  function preconfigStamps() {
    var out = {};
    ((typeof window !== 'undefined' && window.OfferRegistry) || []).forEach(function (o) {
      var ot = o.trigger || {};
      if (ot.type === 'line_property' && ot.key === '_bundle' && ot.value != null) out[String(ot.value)] = 1;
    });
    return out;
  }
  function bundleQty(items, offer) {
    var t = offer && offer.trigger;
    var exact = t && t.type === 'line_property' && t.value != null ? String(t.value) : null;
    var eligible = null;
    if (t && t.type === 'collection_qty') {
      eligible = {};
      (t.productIds || []).forEach(function (id) { eligible[String(id)] = 1; });
    }
    var preconfig = offer && exact == null ? preconfigStamps() : null;
    var n = 0;
    (items || []).forEach(function (i) {
      var p = i.properties || {};
      if (p._offer_gift || p._gift_item === 'true') return; // gifts are never set pieces
      if (i.original_line_price > 0 && i.final_line_price === 0) return; // nor are free items (Daylight Savings' lamp)
      var v = p._bundle;
      if (exact != null) {
        if (v !== exact) return;                            // preconfig: its stamp only
      } else if (offer) {
        // a line STAMPED for a preconfig offer belongs to THAT set, full stop —
        // even though its product is also collection-eligible (Henya's live
        // catch, 2026-08-12: the builder pitch showed under a 30% preconfig set)
        if (v && preconfig && preconfig[String(v)]) return;
        var stampedOurs = !!v;
        var productEligible = eligible && eligible[String(i.product_id)];
        if (!stampedOurs && !productEligible) return;       // the union
      } else if (!v) {
        return;                                             // legacy: any _bundle line
      }
      n += i.quantity || 1;
    });
    return n;
  }

  // The tier earned at a piece count: highest tier whose qty threshold is met.
  function tierFor(offer, qty) {
    var tiers = (offer && offer.reward && offer.reward.tiers) || [];
    var best = null;
    tiers.forEach(function (t) {
      if (qty >= (t.qty || 0) && (!best || (t.qty || 0) > (best.qty || 0))) best = t;
    });
    return best;
  }

  // Shopify discount codes are CASE-INSENSITIVE, and the cart echoes a code in
  // whatever casing it was supplied — live-probed 2026-08-20: arming
  // /discount/wjsconcesale25-bqolry put "wjsconcesale25-bqolry" (lowercase) in
  // cart.discount_codes. Every comparison against the cart's echo must therefore
  // normalize, or a shopper arriving via a lowercased marketing/share link gets
  // our own code misclassified as a CUSTOMER code — which blocks every clear
  // (hasCustomerCode force-falses needClear) AND re-arms the registry casing on
  // every pass (a request loop the breaker can't see, because arms "succeed").
  // Registry-internal comparisons don't need this; the cart-echo boundary does.
  function normCode(c) {
    return String(c == null ? '' : c).trim().toUpperCase();
  }

  // Every code the offer system manages (reward.code + all tier codes, all offers).
  // The system arms/clears ONLY these — a customer-entered code is never touched.
  // Keyed NORMALIZED (see normCode) so a case-variant cart echo still matches.
  function managedCodes() {
    var out = {};
    ((typeof window !== 'undefined' && window.OfferRegistry) || []).forEach(function (o) {
      var r = o.reward || {};
      if (r.code) out[normCode(r.code)] = 1;
      (r.tiers || []).forEach(function (t) { if (t.code) out[normCode(t.code)] = 1; });
    });
    return out;
  }

  function activeOfferCode(cart) {
    var offer = (typeof window !== 'undefined') && window.__activeOffer;
    if (!offer || !offer.reward) return '';
    if (!CODE_REWARD_TYPES[offer.reward.type]) return '';
    // Tiered offer: the EARNED tier's code, from the live count of set pieces —
    // recomputed on every cart change, so editing the set down honestly drops or
    // downgrades the discount (the margin-leak fix). No tier earned -> no code.
    var tiers = offer.reward.tiers || [];
    if (tiers.length) {
      var t = tierFor(offer, bundleQty(cart && cart.items, offer));
      return t && t.code ? String(t.code).trim() : '';
    }
    var code = offer.reward.code;
    if (!withinGetCap(offer, cart)) return '';
    // Blank code = the discount is a server-side automatic (e.g. Regios reads the
    // _bundle_tier line property) — the theme issues NOTHING; the app applies it.
    return (typeof code === 'string' && code.trim()) ? code.trim() : '';
  }

  // One GET unit per trigger unit for a stacking campaign: a plain collection code
  // cannot cap quantity, so the theme refuses to arm it while get-scope units outnumber
  // trigger units (1 lamp + 2 shades: no free shade until a shade comes off). Applies
  // whether the campaign won the cart or is riding alongside the winner.
  function withinGetCap(offer, cart) {
    if (!offer || offer.stacks !== true || !Array.isArray(offer.getProductIds) || !offer.getProductIds.length) return true;
    var units = function (ids) { var n = 0; ((cart && cart.items) || []).forEach(function (i) { var pr = i.properties || {}; if (pr._offer_gift || pr._gift_item === 'true') return; if (ids.indexOf(i.product_id) >= 0) n += (i.quantity || 1); }); return n; };
    return units(offer.getProductIds) <= units((offer.trigger && offer.trigger.productIds) || []);
  }

  // Back-compat: the legacy milestone gift stamps _discount_code on the gift line.
  // Until the gift is a first-class offer reward, honour it when no active-offer
  // code is present. (Suppression means a bundle offer + a gift code never coexist.)
  function legacyGiftCode(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var p = items[i].properties || {};
      if (p._gift_item === 'true' && p._discount_code) return String(p._discount_code);
    }
    return '';
  }

  // The single source of the checkout code to carry for THIS cart/session.
  function checkoutCode(cart) {
    return activeOfferCode(cart) || legacyGiftCode(cart);
  }

  // ── The SITEWIDE SALE code that must ride ALONGSIDE a triggered campaign ──
  //
  // A sale like "25% off all sconces" lives on the SESSION layer. A cart campaign
  // (the set tiers) is an override trigger, so the moment a shopper has 2+ set
  // pieces, __activeOffer becomes the set offer and the sale code stops being
  // armed — the sconce in that same cart silently drops from the advertised 25%
  // to the set's 15/20%. The shopper is charged MORE than the announcement bar
  // promised, which is the worst direction for the error to run.
  //
  // One code cannot fix it: a Shopify percentage code is ONE percent over ONE
  // scope, and this needs two percents over two scopes. Automatics are not an
  // option either — they were retired on 2026-08-10 for beating armed codes at
  // the launch-gate checkout walk. So the cart carries BOTH codes.
  //
  // Live-probed on this (non-Plus) store, 2026-08-20, with throwaway codes on
  // `sconces` + `bundle-eligible`: both landed `applicable: true` and each line
  // took its OWN rate (lamps 29000 -> 24650 = 15%, sconce 27500 -> 20625 = 25%).
  // Two preconditions, both real:
  //   - every code involved must opt into combinesWith.productDiscounts, and
  //   - the two scopes must be DISJOINT. Shopify allows only one product discount
  //     per LINE on non-Plus, so an overlap does not double-discount (it silently
  //     picks one) but it does make which rate a shopper gets unpredictable.
  //     offer-invariants.js enforces the disjointness so config drift can't
  //     reintroduce it quietly.
  //
  // Deliberately narrow: only a `trigger_type: none` + `discount_code` offer with
  // a scope qualifies — i.e. the sitewide sale shape and nothing else. A cart
  // campaign is NOT co-armed with another cart campaign; those still arbitrate to
  // one winner, which is the "one offer per session" model, intact.
  //
  // Narrow on the OTHER side too: the only campaign the sale may ride alongside is
  // a `collection_qty` one (the Build-Your-Set shape). That is the shape this was
  // designed for, the shape the disjoint-scopes invariant actually enforces, and
  // the shape live-probed above.
  //
  // `line_property` is why the gate exists. A blackout-bundle page stamps its
  // lines and arms WOOJNA30-* / WOOJBTS30-*, and BOTH of those are configured
  // combinesWith.productDiscounts: FALSE — deliberately non-combinable, one code
  // per blackout cart. On top of that, `new-apartment-bundle` contains a Helios
  // Sconce, which is in `sconces`, which is the sale's scope — so "disjoint
  // scopes" is false there as well. Co-arming would append a second code the
  // first refuses to sit beside and put the 30% at risk. Blackout carts keep
  // arming exactly ONE code, byte-for-byte as they did before this change.
  var SALE_RIDES_ALONGSIDE = { collection_qty: 1 };
  function sessionSaleCode(cart) {
    var s = (typeof window !== 'undefined') && window.__sessionOffer;
    if (!s || !s.reward || !s.trigger) return '';
    if (s.trigger.type !== 'none') return '';
    if (s.reward.type !== 'discount_code') return '';
    // A cart-content override is in play — only ride alongside the proven shape.
    var a = (typeof window !== 'undefined') && window.__activeOffer;
    if (a && a.id !== s.id && !SALE_RIDES_ALONGSIDE[(a.trigger && a.trigger.type) || 'none']) return '';
    var code = s.reward.code;
    if (typeof code !== 'string' || !code.trim()) return '';
    code = code.trim();
    // Already the primary? Then it is armed by the normal path; not a second code.
    return code === activeOfferCode(cart) ? '' : code;
  }

  // Every code this cart should be carrying, in arm order (primary first).
  // Single-code carts return a 1-element array — the overwhelmingly common case,
  // and byte-for-byte the old behaviour.
  //
  // `primary` is passed in rather than derived because the two callers legitimately
  // disagree on it: syncCartDiscount arms activeOfferCode (the legacy _discount_code
  // gift stamp is already on the line and must not be re-armed as a cart code),
  // while checkoutUrl carries checkoutCode (which does honour that stamp). That
  // difference predates this change; folding it away would be a silent behaviour
  // change to the gift path.
  function withSaleCode(cart, primary) {
    var out = [];
    if (primary) out.push(primary);
    var sale = sessionSaleCode(cart);
    if (sale && out.indexOf(sale) === -1) out.push(sale);
    riderCodes(cart).forEach(function (c) { if (out.indexOf(c) === -1) out.push(c); });
    return out;
  }

  // A buy-X-get-Y campaign RIDES ALONGSIDE whichever campaign won the cart (Sean
  // 9/22, free shade: "both need to work" — 2 cordless + shade = set discount AND
  // the free shade). Its money lives on the GET lines only, so it cannot collide
  // with a lamp-scoped set code; the code itself must be combinesWith.productDiscounts.
  // bxgy_code rewards ride, and so does any coded campaign flagged `stacks` (Shopify
  // reserves a BXGY's buy lamp from other product discounts — decision 2026-09-22 — so the
  // free shade runs as a plain shade-scoped code that stacks). Everything else arbitrates.
  function riderCodes(cart) {
    if (typeof window === 'undefined') return [];
    var R = window.OfferResolver, reg = window.OfferRegistry;
    if (!R || !Array.isArray(reg) || typeof R.triggerMatches !== 'function') return [];
    var active = window.__activeOffer, stamps = {};
    reg.forEach(function (o) { var t = o.trigger || {}; if (t.type === 'line_property' && t.value != null) stamps[String(t.value)] = 1; });
    var out = [];
    reg.forEach(function (o) {
      var r = o.reward || {};
      // Rides: a bxgy_code reward, or any coded campaign flagged `stacks` (offer field).
      if (!(r.type === 'bxgy_code' || (o.stacks === true && CODE_REWARD_TYPES[r.type])) || !r.code || o.enabled === false) return;
      if (active && active.id === o.id) return;
      if (!R.inWindow(o) || !R.triggerMatches(o.trigger, cart, stamps)) return;
      if (!withinGetCap(o, cart)) return;
      out.push(String(r.code).trim());
    });
    return out;
  }
  function desiredCodes(cart) {
    return withSaleCode(cart, checkoutCode(cart));
  }

  // The checkout URL, with the resolved offer's code applied when there is one.
  //
  // `?discount=` carries exactly ONE code, so it cannot express a two-code cart.
  // It does not need to: syncCartDiscount has already armed both onto the CART,
  // and checkout inherits the cart's discount_codes. Passing one of the two here
  // would be worse than passing none — it re-asserts a single code at the moment
  // of checkout and risks dropping the other. So: one code keeps the belt-and-
  // suspenders param, two codes ride the cart.
  function checkoutUrl(cart) {
    var codes = desiredCodes(cart);
    if (!codes.length) return '/checkout';
    if (codes.length === 1) return '/checkout?discount=' + encodeURIComponent(codes[0]);
    // Two codes, but are any actually ON the cart? Normally yes (syncCartDiscount
    // armed them) and re-asserting one at the checkout moment risks dropping the
    // other — bare /checkout is correct. But if NONE armed (arms 429'd, breaker
    // open for the session), bare /checkout charges FULL PRICE against an
    // advertised discount — the worst direction. With zero armed there is no
    // second code to drop, so carrying the PRIMARY beats carrying nothing.
    var anyArmed = ((cart && cart.discount_codes) || []).some(function (d) {
      var n = normCode(d && d.code);
      return codes.some(function (c) { return normCode(c) === n; });
    });
    return anyArmed ? '/checkout' : '/checkout?discount=' + encodeURIComponent(codes[0]);
  }

  function codeInCart(cart, code) {
    // applicable !== false matters: Shopify lists an armed-but-REFUSED code
    // (below its server min-qty floor) in discount_codes either way. Without
    // this check the set chip said "30% off applied" while checkout charged
    // full price (sweep #4 — a customer edits a preconfig set down to 1 piece).
    return ((cart && cart.discount_codes) || []).some(function (d) {
      return d && normCode(d.code) === normCode(code) && d.applicable !== false;
    });
  }

  function clearCartDiscounts() {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: '' })
    }).then(assertOk);
  }

  // Sync the cart's discount codes to the EARNED state (tier-aware, not just at
  // checkout) so cart.js — and therefore the drawer — always shows the truth:
  //   earned code missing         -> arm it; clear OUR stale codes first (codes
  //   earned tier changed            append, they don't replace — live-probed)
  //   nothing earned but one of   -> clear (update.js {discount:""}) — the
  //   OUR codes is still applied     margin-leak fix: edit below tier, discount drops
  // Only codes in managedCodes() are ever touched — a cart carrying a customer-
  // entered code is not ours to clear. Idempotent -> converges. Belt+suspenders:
  // the codes ALSO carry server-side min-quantity floors (SETTEST15 3+, SETTEST20
  // 4+), so even a missed sync can't discount a below-tier cart.
  var isSyncingDiscount = false;
  function syncCartDiscount(cart, opts) {
    if (typeof fetch !== 'function' || isSyncingDiscount) return Promise.resolve(false);
    // A cart can legitimately need TWO codes: a triggered campaign plus the
    // sitewide sale riding alongside it (see sessionSaleCode). Everything below
    // is set logic over `desired` rather than equality against one string.
    var desired = withSaleCode(cart, activeOfferCode(cart));
    // Set membership is computed over NORMALIZED codes (the cart echoes whatever
    // casing armed the code — see normCode); arming still uses registry casing.
    var wanted = {};
    desired.forEach(function (c) { wanted[normCode(c)] = 1; });
    var applied = ((cart && cart.discount_codes) || []).map(function (d) { return normCode(d && d.code); });
    var managed = managedCodes();
    var staleManaged = applied.filter(function (c) { return managed[c] && !wanted[c]; });
    var hasCustomerCode = applied.some(function (c) { return c && !managed[c]; });

    var missing = desired.filter(function (c) { return applied.indexOf(normCode(c)) === -1; });
    // never clear a cart holding a customer-entered code (clearing is all-or-nothing)
    var needClear = staleManaged.length > 0 && !hasCustomerCode;
    // Clearing is all-or-nothing, so it takes the wanted codes down with the stale
    // one — after a clear, EVERY desired code has to be re-armed, not just the
    // ones that were missing beforehand.
    var toArm = needClear ? desired : missing;
    // A newly needed free-item code arms alone: the lines it frees stop counting as set pieces
    // only once Shopify applies it, so the set tier is settled on the next pass.
    var riders = riderCodes(cart).map(normCode);
    var newRiders = missing.filter(function (c) { return riders.indexOf(normCode(c)) !== -1; });
    if (newRiders.length) { needClear = false; toArm = newRiders; }
    if (!toArm.length && !needClear) return Promise.resolve(false);

    isSyncingDiscount = true;
    var mutated = false;
    var chain = Promise.resolve();
    if (needClear) chain = chain.then(function () { return clearCartDiscounts().then(function () { mutated = true; }); });
    // Sequential, not Promise.all: /discount/ APPENDS, and concurrent appends to
    // the same cart race (the write-discipline rule this file already follows).
    toArm.forEach(function (code) {
      chain = chain.then(function () {
        return fetch('/discount/' + encodeURIComponent(code), { credentials: 'same-origin' })
          .then(assertOk)
          .then(function () { mutated = true; });
      });
    });
    return chain
      .then(function () {
        isSyncingDiscount = false;
        if (wantRefresh(opts)) refreshCart();
        return true;
      })
      .catch(function () {
        isSyncingDiscount = false;
        lastPassFailed = true;
        // A PARTIAL failure (clear landed, arm didn't) changed server state — one
        // refresh so the UI re-syncs to server truth instead of lying.
        if (mutated) { lastPassMutated = true; if (wantRefresh(opts)) refreshCart(); }
        return false;
      });
  }

  // Does `id` name a registry offer that is merely OUT OF WINDOW (as opposed to
  // one that no longer exists)? Uses the resolver's own window rule so "expired"
  // means exactly what it means everywhere else.
  function expiredButKnown(id) {
    var reg = (typeof window !== 'undefined' && window.OfferRegistry) || [];
    var R = typeof window !== 'undefined' && window.OfferResolver;
    if (!R || !R.inWindow) return false;
    for (var i = 0; i < reg.length; i++) {
      if (reg[i].id === id) return !R.inWindow(reg[i]);
    }
    return false;
  }

  // Persist the resolved SESSION offer to cart.attributes._offer — the durable,
  // order-bound carrier (attribution / offer:<id>). Reads the SESSION layer
  // (window.__sessionOffer), never the display offer — a triggered cart campaign
  // (builder set) must not stop the sitewide session from persisting (sweep C2:
  // reading __activeOffer skipped persistence whenever a bundle was in cart).
  // Only once the cart is non-empty (avoids a cart-per-visitor; sessionStorage
  // covers pre-cart stickiness). Idempotent — once both attributes match, no-op.
  //
  // Also persists `_offer_by` — HOW the session was acquired ('landing' = chosen
  // via a blackout page, 'default' = the site-wide offer landed on them). The
  // resolver reads it to decide whether a dated site-wide default (Labor Day) may
  // supersede a sticky session: ambient yes, deliberate no. Provenance has to be
  // recorded at acquisition because it cannot be recovered later — a gift swap
  // makes yesterday's default indistinguishable from a deliberate pick.
  function persistSessionOffer(cart, opts) {
    var offer = (typeof window !== 'undefined') && window.__sessionOffer;
    if (!offer || !cart || !cart.items || !cart.items.length) return Promise.resolve(false);
    var by = offer.resolvedBy;
    if (by !== 'session' && by !== 'landing' && by !== 'default') return Promise.resolve(false);
    var attrs = cart.attributes || {};
    var current = attrs._offer || null;
    // Provenance is WRITE-ONCE, recorded at acquisition — it rides along with the
    // `_offer` write and is never revised while the id holds. Two reasons, both
    // load-bearing:
    //
    //   1. It is only knowable at acquisition. `by === 'session'` means the sticky
    //      value was REPLAYED (including out of the sessionStorage pre-cart
    //      buffer), and a replay carries no provenance of its own — the only way
    //      to score it is to re-infer from is_default, which is exactly the signal
    //      provenance exists to override. A cart that acquired a default-flagged
    //      offer DELIBERATELY off a blackout landing (the G2 shape) would be
    //      re-scored 'default' on the very next pageview, flipping the session
    //      from deliberate to ambient and handing it to the next dated campaign —
    //      silently undoing the resolver rule this attribute was added to feed.
    //   2. Re-deriving invites a rewrite loop; matching on the id cannot.
    //
    // A pre-provenance cart (`_offer` set, no `_offer_by`) is deliberately NOT
    // backfilled: a write per legacy cart buys nothing the resolver's is_default
    // fallback (sessionWasAmbient) doesn't already infer for exactly this case.
    var prov = by === 'session' ? (offer.is_default ? 'default' : 'landing') : by;
    if (current === offer.id) return Promise.resolve(false);
    // EXPIRED IS NOT SUPERSEDED. Once an offer ends, the session resolves to the
    // next one — but the cart it was earned in must keep naming the offer that
    // earned it: `_offer` is the attribution + clawback carrier CS reads off the
    // order (docs/launch-runbook.md:49, docs/cutover-2026-08-13.md:53). A Labor
    // Day cart converting Sept 10 has to still say Labor Day. Only a sticky id
    // that is genuinely GONE from the registry gets overwritten.
    if (current && current !== offer.id && expiredButKnown(current)) return Promise.resolve(false);
    if (typeof fetch !== 'function') return Promise.resolve(false);
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _offer: offer.id, _offer_by: prov } })
    })
      .then(assertOk)
      .then(function () {
        if (wantRefresh(opts)) refreshCart();
        return true;
      })
      .catch(function () { lastPassFailed = true; return false; });
  }

  // ── GWP: add the gift variant for the active offer (the second primitive) ──
  // gwp_free_variant ($0) or gwp_coded (real-priced + a code that prices it). We
  // manage ONLY lines we stamp (_offer_gift = offer id) — never the legacy milestone
  // gift lines (which lack _offer_gift), so the two engines don't fight. The gift is
  // added once the PURCHASE subtotal (cart minus our gift lines) meets
  // reward.min_subtotal (allium $100; wavy 0 = any). gwp_coded REFUSES to add without
  // its code (Henya's footgun: no code -> customer charged).
  //
  // `discount_code` is in the set too, and that is the whole of B5. A site-wide
  // sale is a discount_code offer, so the moment one becomes the session offer
  // (B1) a gift-only gate turns the GWP OFF for the length of the sale — the gift
  // would vanish on Sept 1 and come back on the 9th, with no one having asked for
  // that. A reward can carry BOTH: the code prices the sale, `reward_variant`
  // still adds the gift. An offer with no `reward_variant` is unaffected (the
  // variant_id check below is what actually gates the gift).
  //
  // CONTRACT: a gift variant is a $0 variant. gwp_coded is the exception that
  // proves it — it carries a real-priced variant and REFUSES to add without the
  // code that zeroes it. `discount_code` codes discount the ORDER, not one line,
  // so a real-priced gift would simply be sold at 20% off. Locked by the $0
  // assertion in tests/gwp-e2e.spec.js.
  var GIFT_REWARD_TYPES = { gwp_free_variant: 1, gwp_coded: 1, discount_code: 1 };
  var isSyncingGift = false;

  // What the shopper is spending, for the gift threshold.
  //
  // ORIGINAL line price, not final: once a site-wide code is armed, `final_line_price`
  // is the DISCOUNTED price, so a $100 gift threshold silently becomes $125 of
  // goods during a 20% sale — the customer clears the advertised bar and the gift
  // doesn't appear. The threshold is a statement about what they chose to buy.
  // (No-op until a site-wide code exists, which B5 is what creates.)
  function purchaseSubtotal(items) {
    var sum = 0;
    (items || []).forEach(function (i) {
      if ((i.properties || {})._offer_gift) return; // exclude our gifts from the threshold
      // AFTER discounts (Sean 9/11): the rung reads what the shopper actually pays for merchandise
      if (i.final_line_price != null) sum += i.final_line_price;
      else sum += i.line_price != null ? i.line_price : i.original_line_price || 0;
    });
    return sum;
  }

  // Gift rungs: reward.gift_tiers when present, else the single legacy rung at min_subtotal.
  // Sorted ascending by threshold; qty >= 1; malformed entries dropped.
  // Rungs live ONCE, on the sitewide gift offer. A code offer that carries the same gift
  // variant (a blackout sale with the gift attached) inherits them, so its session earns
  // the same tiers and talks about the gift, not its own discount (live-caught 9/11).
  function giftProgram(offer) {
    var r = (offer && offer.reward) || {};
    if (Array.isArray(r.gift_tiers) && r.gift_tiers.length) return r.gift_tiers;
    var reg = (typeof window !== 'undefined' && Array.isArray(window.OfferRegistry)) ? window.OfferRegistry : [];
    var owner = reg.filter(function (o) {
      var or = (o && o.reward) || {};
      return !!o && !!o.is_default && !!o.enabled && o.id !== (offer && offer.id) && String(or.variant_id) === String(r.variant_id) && Array.isArray(or.gift_tiers) && or.gift_tiers.length > 0;
    })[0];
    return owner ? owner.reward.gift_tiers : null;
  }
  function giftRungs(offer) {
    var r = (offer && offer.reward) || {};
    var raw = giftProgram(offer);
    var rungs = (raw || []).map(function (t) {
      return { min_subtotal: parseInt(t && t.min_subtotal, 10) || 0, qty: parseInt(t && t.qty, 10) || 1, locked: (t && t.locked) || '', unlocked: (t && t.unlocked) || '', name: (t && t.name) || '' };
    }).filter(function (t) { return t.qty >= 1; });
    // A rung may carry its own window (window_start / window_end, date-only = local
    // calendar day, end inclusive); it is evaluated through OfferResolver.inWindow like
    // every other window (decision 2026-08-11), so a dated rung retires itself.
    var R = typeof window !== 'undefined' && window.OfferResolver;
    rungs = rungs.filter(function (t, i) {
      var src = (raw || [])[i] || {};
      var ws = src.window_start || '', we = src.window_end || '';
      if (!ws && !we) return true;
      if (!R || typeof R.inWindow !== 'function') return true;
      return R.inWindow({ window: { start: ws, end: we } });
    });
    if (!rungs.length) rungs = [{ min_subtotal: r.min_subtotal || 0, qty: 1, locked: '', unlocked: '' }];
    rungs.sort(function (a, b) { return a.min_subtotal - b.min_subtotal; });
    return rungs;
  }
  function earnedRung(offer, items) {
    var sub = purchaseSubtotal(items), best = null;
    giftRungs(offer).forEach(function (t) { if (sub >= t.min_subtotal) best = t; });
    return best;
  }
  function wantedGift(offer, items) {
    if (!offer || !offer.reward || !GIFT_REWARD_TYPES[offer.reward.type] || !offer.reward.variant_id) return null;
    if (offer.reward.type === 'gwp_coded' && !(offer.reward.code && String(offer.reward.code).trim())) return null; // footgun guard
    var hasPurchase = (items || []).some(function (i) { return !(i.properties || {})._offer_gift; });
    if (!hasPurchase) return null;
    var rung = earnedRung(offer, items);
    if (!rung) return null;
    return { variantId: String(offer.reward.variant_id), offerId: offer.id, qty: rung.qty };
  }


  // The gift keys off the SITEWIDE SESSION layer (Sean's model, 2026-08-11): a
  // builder set stacks with the GWP because the session is still the GWP; a
  // preconfig-bundle session gets no gift because its blackout landing REPLACED
  // the sitewide offer. A cart campaign can additionally opt out via its
  // suppression list (naming the session offer's id or 'gwp') — config, not code.
  function giftOffer() {
    if (typeof window === 'undefined') return null;
    var session = window.__sessionOffer;
    if (!session) return null;
    // "the offer whose gift applies" — a session that cannot carry a gift has none
    // by definition. BOTH halves of that are checked here: a gift-capable reward
    // type AND an actual `reward_variant`. The type check alone was enough while
    // the capable types were gift-only, but B5 admits `discount_code`, and every
    // blackout bundle on the site is a variant-less `discount_code` — so a type-only
    // gate started answering "yes, a gift applies" for offers that have no gift,
    // and the blackout landing pages silently claimed a GWP (caught by
    // tests/fixed-bundle.spec.js:53). Same map AND same variant rule as wantedGift,
    // so the promise and the applicator cannot drift.
    var rt = session.reward && session.reward.type;
    if (!GIFT_REWARD_TYPES[rt] || !session.reward.variant_id) return null;
    var campaign = window.__activeOffer;
    if (campaign && campaign.id !== session.id) {
      var sup = campaign.suppression || [];
      if (sup.indexOf(session.id) !== -1 || sup.indexOf('gwp') !== -1) return null;
    }
    return session;
  }

  function syncGift(cart, opts) {
    if (isSyncingGift) return Promise.resolve(false);
    var items = (cart && cart.items) || [];
    var ourGifts = items.filter(function (i) { return (i.properties || {})._offer_gift; });
    var want = wantedGift(giftOffer(), items);
    var wantedLines = want ? ourGifts.filter(function (i) {
      return String(i.variant_id) === want.variantId && (i.properties || {})._offer_gift === want.offerId;
    }) : [];
    var haveWanted = wantedLines.length > 0;
    var toRemove = ourGifts.filter(function (i) {
      return !want || String(i.variant_id) !== want.variantId || (i.properties || {})._offer_gift !== want.offerId;
    });
    // Self-heal a raced double-add: the wanted gift must be qty 1 (a stale-snapshot
    // race can add.js the same line twice -> Shopify increments qty).
    var toFixQty = wantedLines.filter(function (i) { return (i.quantity || 1) !== want.qty; }); // both directions: rung earned or lost
    // NOTHING TO DO -> false, and crucially NO cart:refresh (a previous version
    // fired refresh unconditionally -> an infinite request loop; probe-caught).
    var needAdd = !!(want && !haveWanted);
    if (!needAdd && !toRemove.length && !toFixQty.length) return Promise.resolve(false);
    if (typeof fetch !== 'function') return Promise.resolve(false);

    isSyncingGift = true;
    var mutated = false;
    var chain = Promise.resolve();
    toRemove.concat(toFixQty).forEach(function (i) {
      var qty = toFixQty.indexOf(i) === -1 ? 0 : want.qty;
      chain = chain.then(function () {
        return fetch('/cart/change.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: i.key, quantity: qty })
        }).then(assertOk).then(function () { mutated = true; });
      });
    });
    if (needAdd) {
      chain = chain.then(function () {
        return fetch('/cart/add.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: want.variantId, quantity: want.qty, properties: { _gift_item: 'true', _offer_gift: want.offerId } }] })
        }).then(assertOk).then(function () { mutated = true; });
      });
    }
    return chain.then(function () {
      isSyncingGift = false;
      if (wantRefresh(opts)) refreshCart();
      return true;
    }).catch(function () {
      isSyncingGift = false;
      lastPassFailed = true;
      if (mutated) { lastPassMutated = true; if (wantRefresh(opts)) refreshCart(); }
      return false;
    });
  }

  // ── THE ORCHESTRATOR: one pass, sequential writers, one refresh, a breaker ──
  // Called by the drawer on every cart change. Runs discount -> gift -> attribute
  // IN ORDER (no interleaving -> no stale-snapshot double-writes), fires at most
  // ONE cart:refresh (only if something actually changed server-side), and trips
  // a breaker after MAX_FAIL_PASSES consecutive failing passes so a persistent
  // failure (sold-out gift, deleted code, 429) can never loop.
  var MAX_FAIL_PASSES = 3;
  var failPasses = 0;
  var isSyncingAll = false;
  var lastPassFailed = false;   // set by writers' catch handlers
  var lastPassMutated = false;  // set by writers when a partial failure still wrote

  function syncAll(cart) {
    if (isSyncingAll) return Promise.resolve(false);
    if (failPasses >= MAX_FAIL_PASSES) return Promise.resolve(false); // breaker open
    isSyncingAll = true;
    lastPassFailed = false;
    lastPassMutated = false;
    var noRefresh = { refresh: false };
    var didAny = false;
    return syncCartDiscount(cart, noRefresh)
      .then(function (a) { didAny = didAny || a; return syncGift(cart, noRefresh); })
      .then(function (b) { didAny = didAny || b; return persistSessionOffer(cart, noRefresh); })
      .then(function (c) {
        didAny = didAny || c;
        isSyncingAll = false;
        if (lastPassFailed) {
          failPasses++;
          if (failPasses >= MAX_FAIL_PASSES && typeof console !== 'undefined') {
            console.warn('[offer] cart writes failing repeatedly — offer sync paused for this session');
          }
        } else {
          // ANY clean pass resets — including a no-op. The old `else if (didAny)`
          // let no-op passes carry the count, so 3 transient 429s spread across a
          // whole session (fail, hours of healthy no-ops, fail, fail) opened the
          // breaker permanently — against the header's own "consecutive failing
          // passes" contract. A no-op pass is proof the writers are healthy.
          failPasses = 0;
        }
        // one refresh per pass: on success-with-changes, or after a partial
        // failure that still changed server state (re-sync the UI to truth).
        if ((didAny && !lastPassFailed) || lastPassMutated) refreshCart();
        return didAny;
      })
      .catch(function () { isSyncingAll = false; failPasses++; return false; });
  }

  window.OfferReward = {
    giftRungs: giftRungs,
    purchaseSubtotal: purchaseSubtotal,
    checkoutCode: checkoutCode,
    checkoutUrl: checkoutUrl,
    activeOfferCode: activeOfferCode,
    sessionSaleCode: sessionSaleCode,
    // exported for the LOCKSTEP test only (offer-sale-rides-campaign.spec.js):
    // the invariant module's SALE_RIDES_TRIGGERS must name the same trigger
    // types, or the disjointness rule stops guarding what the gate admits.
    SALE_RIDES_ALONGSIDE: SALE_RIDES_ALONGSIDE,
    desiredCodes: desiredCodes,
    codeInCart: codeInCart,
    syncCartDiscount: syncCartDiscount,
    persistSessionOffer: persistSessionOffer,
    wantedGift: wantedGift,
    giftOffer: giftOffer,
    syncGift: syncGift,
    syncAll: syncAll,
    bundleQty: bundleQty,
    tierFor: tierFor,
    managedCodes: managedCodes,
    clearCartDiscounts: clearCartDiscounts
  };
})();
